import React from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import { ACTIVE } from "../constant/constant";
import emptyImg from "../assets/images/empty.png"
import { BRAND } from "../constant/theme";

/**
 * ##Protected Route Components
 * @returns
 */
export const PrivateRoutes = () => {
  const auth = localStorage.getItem("token");
  return auth ? (
    <>
      <Navigate to={"/dashboard"} />
      <Outlet />
    </>
  ) : (
    <Navigate to={"/login"} />
  );
};

export const NewPrivateRoute = ({ children, ...rest }) => {
  const auth = localStorage.getItem("token");
  return <Route {...rest}>{auth ? children : <Navigate to="/login" />}</Route>;
};

/**
 * ##Validation Message Handling
 * @param {*} param0
 * @returns
 */
export const ValidationMessage = ({ error, name }) => {
  if (!error || !Object.prototype.hasOwnProperty.call(error, name)) {
    return null;
  }
  const message = error[name]?.[0];
  if (!message) return null;
  return <span className="app-form-error">{message}</span>;
};

/**
 * ## Pagination Component
 * @param {*} param
 * @returns
 */

export const Pagination = ({
  paginator,
  page,
  perPage,
  setPerPage,
  className = "",
  entityName = "entries",
}) => {
  if (!paginator) return null;

  const displayRange = 5;
  const halfRange = Math.floor(displayRange / 2);

  const current = paginator.current;
  const last = paginator.last;
  const total = paginator.total;

  // Calculate items per page from total/last
  const itemsPerPage = perPage || Math.ceil(total / last);

  // Calculate the "Showing X to Y of Z" range
  const from = (current - 1) * itemsPerPage + 1;
  const to = Math.min(current * itemsPerPage, total);

  const startPage = Math.max(1, current - halfRange);
  const endPage = Math.min(last, startPage + displayRange - 1);

  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPerPage && setPerPage(newPerPage);
    page(1); // reset to first page when rows per page changes
  };

  const isPrevDisabled = current <= 1;
  const isNextDisabled = current >= last;

  return (
    <div className={`table-controls-row ${className}`}>
      <div className="table-entries-info">
        Showing {from} to {to} of {total} {entityName}
      </div>
      <div className="pagination-wrapper">
        <div className="rows-per-page-container">
          <span>Show</span>
          <select
            className="rows-per-page-select"
            value={itemsPerPage}
            onChange={handlePerPageChange}
            aria-label="Rows per page"
          >
            {[10, 25, 50, 100].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          <span>rows per page</span>
        </div>
        <ul className="custom-pagination">
          <li>
            <button
              type="button"
              className={`page-btn${isPrevDisabled ? " disabled" : ""}`}
              title="Previous Page"
              onClick={() => page(Math.max(1, current - 1))}
            >
              &laquo;
            </button>
          </li>
          {Array.from({ length: endPage - startPage + 1 }, (_, i) => (
            <li key={startPage + i}>
              <button
                type="button"
                className={`page-btn${current === startPage + i ? " active" : ""}`}
                onClick={() => page(startPage + i)}
              >
                {startPage + i}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className={`page-btn${isNextDisabled ? " disabled" : ""}`}
              title="Next Page"
              onClick={() => page(Math.min(last, current + 1))}
            >
              &raquo;
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};
/**
 * ## set badge class active while data status is Active
 * @param {*} status
 * @returns
 */
export const setBadgeClass = (status) => {
  if (status === ACTIVE) return "badge bg-label-info me-1";
  return "badge bg-label-warning me-1";
};

/**
 * Table Data Not Found means show the message
 * @param {*} param0
 * @returns
 */
// export const NoDataFound = ({ front, back, message }) => {
//   return (
//     <tr>
//       <td colSpan={front}> </td>
//       <td>{message}</td>
//       <td colSpan={back}> </td>
//     </tr>
//   );
// };

export const NoDataFound = ({ colspan, message }) => {
  return (
    <tr>
      <td className="center" colSpan={colspan}>{message}</td>
    </tr>
  );
};

export const NoUserFound = ({ title = "No data found", subtitle = "There is no data available to show at this time." }) => {
  return (
    <div className="not-found-container" style={{ textAlign: "center", padding: "20px 20px 40px" }}>
      <div className="not-found-icon-wrapper" style={{ marginBottom: "16px" }}>
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block" }}>
          <g transform="rotate(-15 36 43)">
            <rect x="18" y="22" width="36" height="42" rx="8" fill="#F1F5F9" />
          </g>
          <g transform="rotate(10 48 37)" style={{ filter: "drop-shadow(0px 8px 16px rgba(100, 116, 139, 0.15))" }}>
            <rect x="30" y="16" width="36" height="42" rx="8" fill="#FFFFFF" />
            <rect x="38" y="30" width="20" height="5" rx="2.5" fill="#E2E8F0" />
            <rect x="38" y="40" width="12" height="5" rx="2.5" fill="#E2E8F0" />
          </g>
        </svg>
      </div>
      <h5 style={{ color: BRAND.primary, fontWeight: "700", fontSize: "1.125rem", marginBottom: "8px" }}>
        {title}
      </h5>
      <p style={{ color: "#333", fontSize: "0.875rem", margin: 0, marginLeft: "auto", marginRight: "auto", lineHeight: "1.6" }}>
        {subtitle}
      </p>
    </div>
  );
};

/**
 * frame the react select options
 * @param {*} data
 * @param {*} key
 * @param {*} value
 * @returns
 */
export const frameDataOptions = (data, key, value) => {
  if (data.length > 0) {
    return data.map((rs) => ({ value: rs[key], label: rs[value] }));
  }
  return [];
};

/**
 * selected the react-select value pluck
 * @param {*} data
 * @returns
 */
export const pluckValue = (data) => {
  let response = [];
  if (data.length > 0) {
    data.forEach((value) => response.push(value.value));
  }
  return response;
};

export const checkEmptyOrNull = (data) => {
  return data !== "" && data !== null && data !== undefined ? data : "-";
};

export const weekDaysInText = (day) => {
  let day_text = "";
  if (day === 0) day_text = "Sunday";
  else if (day === 1) day_text = "Monday";
  else if (day === 2) day_text = "Tuesday";
  else if (day === 3) day_text = "Wednesday";
  else if (day === 4) day_text = "Thursday";
  else if (day === 5) day_text = "Friday";
  else if (day === 6) day_text = "Saturday";
  return day_text;
}

export const meetingWeeksInText = (week) => {
  let week_text = "";
  if (week === 1) week_text = "1st Week";
  else if (week === 2) week_text = "2nd Week";
  else if (week === 3) week_text = "3rd Week";
  else if (week === 4) week_text = "4th Week";
  else if (week === 5) week_text = "5th Week";
  return week_text;
}

export const WrapText = (text, maxLength) => {
  const words = text.split(' ');

  let currentLength = 0;
  let wrappedText = [];

  for (const word of words) {
    if (currentLength + word.length > maxLength) {
      wrappedText.push(<br key={wrappedText.length} />);
      currentLength = 0;
    }

    wrappedText.push(word + ' ');
    currentLength += word.length + 1; // 1 is for the space between words
  }

  return wrappedText;
};

export const hasRole = (roles) => {
  if (!Array.isArray(roles)) return false;
  const auth_user = JSON.parse(localStorage.getItem("user"));
  if (roles.includes(auth_user?.user_type)) {
    return true;
  } else {
    return false;
  }
}

export const getRoleName = (user_type) => {
  if (user_type === 1) {
    return "Super Admin";
  } else if (user_type === 6) {
    return "Country Admin";
  } else if (user_type === 7) {
    return "State Admin";
  } else if (user_type === 2) {
    return "Region Admin";
  } else if (user_type === 3) {
    return "Chapter Admin";
  } else if (user_type === 4) {
    return "Member";
  } else {
    return "";
  }
}

export const getOrdinalSuffix = (i) => {
  const j = i % 10,
        k = i % 100;
  if (j === 1 && k !== 11) {
    return i + "st";
  }
  if (j === 2 && k !== 12) {
    return i + "nd";
  }
  if (j === 3 && k !== 13) {
    return i + "rd";
  }
  return i + "th";
};